import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AppBar,
  Box,
  Container,
  Dialog,
  DialogContent,
  Grid,
  Toolbar,
  Typography
} from "@mui/material";
import { Snackbar as AppSnackbar } from "../../components/Snackbar";

import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Button } from "../../components/Button";
import { JobSelector } from "../../components/selectors/JobSelector";
import { SettingsJob } from "../../data/settings/SettingsJob";
import useDebounce from "../../hooks/useDebounce";
import { useGetIndustryStandardJob } from "../../hooks/useGetIndustryStandardJob";
import { useGetListJob } from "../../hooks/useGetJob";
import { useLocale } from "../../providers/LocaleProvider";
import CloseIcon from "../../resources/images/icons-close.svg";

import { AxiosResponse } from "axios";
import { ConfirmationDialog, Theme } from "klayowebshared";
import { concat, isEmpty } from "lodash";
import { AppContext } from "../../common/AppContext";
import StandardLibraryCheck from "../../components/StandardLibraryCheck";
import { JobBenchmarkingContext } from "../../contexts/JobBenchmarkingContext";
import { useAppMutation } from "../../hooks/useAppMutation";
import { useAppQuery } from "../../hooks/useAppQuery";
import {
  IJobBenchmarking,
  IJobItem,
  SaveJobBenchmarkingPayload,
  SaveJobBenchmarkingResponse
} from "../../interfaces/Job";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { JobBenchMarkingSchema, JobBenchMarkingType } from "../../utilities/validationSchema";
import BenchmarkComparisonResult from "./BenchmarkComparisonResult";
import ImportCompeteniesErrorDialog from "./ImportCompeteniesErrorDialog";
import JobBenchmarkingProgress from "./JobBenchmarkingProgress";

type Props = {
  onClose: () => void;
  open: boolean;
  selectedJob?: SettingsJob;
  selectedStandardJob?: IJobItem;
  theme: typeof Theme;
};

const defaultValues: JobBenchMarkingType = {
  jobDescription: undefined,
  renamedTasks: [],
  renamedCompetencies: [],
  addedStandardMatchedTasks: [],
  addedStandardUnmatchedTasks: [],
  addedStandardMatchedCompetencies: [],
  addedStandardUnmatchedCompetencies: [],
  addedStandardMatchedTaskCompetencies: []
};

const JobBenchmarking = ({ onClose, open, selectedJob, theme, selectedStandardJob }: Props) => {
  const [yourJob, setYourJob] = useState<SettingsJob | undefined>(selectedJob);
  const [industryStandardJob, setIndustryStandardJob] = useState<IJobItem | undefined>(
    selectedStandardJob
  );
  const [showConfirmSaveChanges, setShowConfirmSaveChanges] = useState(false);
  const [selectedDraftYourJob, setSelectedDraftYourJob] = useState<SettingsJob | undefined>();
  const [selectedDraftStandardJob, setSelectedDraftStandardJob] = useState<IJobItem | undefined>();
  const previousIndustryStandardJobRef = useRef<IJobItem | undefined>(selectedStandardJob);
  const [isChangedError, setIsChangedError] = useState(false);
  const [isShowChangedDetail, setIsShowChangedDetail] = useState(false);
  const [competenciesError, setCompetenciesError] = useState<string[]>([]);
  const [isChangedIndustryStandardJob, setIsChangedIndustryStandardJob] = useState(false);
  const { organization } = useContext(AppContext);
  const { translate } = useLocale();

  const form = useForm<JobBenchMarkingType>({
    resolver: zodResolver(JobBenchMarkingSchema),
    defaultValues,
    mode: "onChange"
  });

  const {
    handleSubmit,
    formState: { dirtyFields }
  } = form;

  const isChangedForm =
    !isEmpty(dirtyFields) || Object.values(dirtyFields).some((isDirty) => isDirty);

  const [inputSearch, setInputSearch] = useState({
    job: "",
    standardJob: ""
  });

  const jobDebounce = useDebounce(inputSearch.job, 500);
  const standardJobDebounce = useDebounce(inputSearch.standardJob, 500);

  const { jobList } = useGetListJob({ name: jobDebounce });
  const { jobStandardLibrary } = useGetIndustryStandardJob({
    name: standardJobDebounce,
    enabled: organization?.enableStandardsLibrary
  });
  const { showSnackbar } = useSnackbar();
  const { resetToDefault } = useContext(JobBenchmarkingContext);

  const isValidJSelectedJobs = useMemo(
    () => !isEmpty(yourJob) && !isEmpty(industryStandardJob),
    [yourJob, industryStandardJob]
  );

  const { data, isFetching } = useAppQuery<IJobBenchmarking>(
    `/StandardLibrary/jobs/benchmark/${yourJob?.id}/${industryStandardJob?.id}`,
    {
      enabled: isValidJSelectedJobs,
      staleTime: 0
    }
  );

  const { mutateAsync: onSaveJob, isPending: isLoading } = useAppMutation<
    AxiosResponse<SaveJobBenchmarkingResponse>,
    any,
    Partial<SaveJobBenchmarkingPayload>
  >("put", "/StandardLibrary/jobs/benchmark/update", {
    config: {
      showSnackbar: false
    }
  });

  const handleInputChangeJob = useCallback((e: string) => {
    setInputSearch((prev) => ({ ...prev, job: e }));
  }, []);

  const handleInputChangeStandardJob = useCallback((e: string) => {
    setInputSearch((prev) => ({ ...prev, standardJob: e }));
  }, []);

  const onSubmit = async (values: JobBenchMarkingType) => {
    setIsShowChangedDetail(false);
    try {
      const payload: SaveJobBenchmarkingPayload = {
        jobDefinitionId: yourJob?.id || "",
        description: values.jobDescription || "",
        updateTasks:
          values.renamedTasks?.map((t) => ({
            name: t.name,
            jobTaskId: t.id
          })) || [],
        updateCompetencies:
          values.renamedCompetencies?.map((c) => ({
            name: c.name,
            jobAttributeId: c.id
          })) || [],
        newTasks:
          concat(values.addedStandardUnmatchedTasks, values.addedStandardMatchedTasks)?.map(
            (t) => t?.id || ""
          ) || [],
        newCompetencies:
          concat(
            values.addedStandardUnmatchedCompetencies,
            values.addedStandardMatchedCompetencies,
            values.addedStandardMatchedTaskCompetencies
          )?.map((c) => c?.id || "") || []
      };

      await onSaveJob(payload);

      showSnackbar(translate("settingsViews.jobBenchmarking.successfullyUpdatedJob"), "success");

      restoreDraftJobsOrClose();
    } catch (error: any) {
      setCompetenciesError(error?.response?.data?.invalidCompetencies);
      // showSnackbar("Failed to updated job", "error");
      setIsShowChangedDetail(true);
      if (isChangedIndustryStandardJob) {
        revertToPreviousIndustryStandardJob();
      }
    }
  };

  const renderContent = useCallback(() => {
    if (isFetching) {
      return <JobBenchmarkingProgress isLoading={isFetching} />;
    }

    if (data && isValidJSelectedJobs) {
      return <BenchmarkComparisonResult theme={theme} results={data} />;
    }

    return <JobBenchmarkingProgress isLoading={false} />;
  }, [data, isFetching, isValidJSelectedJobs, theme]);

  const onShowConfirmClose = () => {
    if (isChangedForm) {
      return setShowConfirmSaveChanges(true);
    }
    onClose();
  };

  const onCancelApplyChanges = () => {
    setShowConfirmSaveChanges(false);
    restoreDraftJobsOrClose();
  };

  const onAcceptApplyChanges = async () => {
    setShowConfirmSaveChanges(false);
    // send payload to API
    const values = form.getValues();
    await onSubmit(values);
  };

  const restoreDraftJobsOrClose = () => {
    if (selectedDraftYourJob) {
      setYourJob(selectedDraftYourJob);
      setSelectedDraftYourJob(undefined);
    }
    if (selectedDraftStandardJob) {
      setIndustryStandardJob(selectedDraftStandardJob);
      setSelectedDraftStandardJob(undefined);
    }
    if (selectedDraftYourJob || selectedDraftStandardJob) {
      onResetForm();
    } else {
      onClose();
    }
  };

  const onResetForm = () => {
    form.reset(defaultValues);
    resetToDefault();
  };

  const onYourJobChange = (e: any, v: any | null) => {
    if (isChangedForm) {
      setSelectedDraftYourJob(v || undefined);
      setShowConfirmSaveChanges(true);
    } else {
      setYourJob(v || undefined);
      onResetForm();
    }
  };

  const onStandardJobChange = (e: any, v: any | null) => {
    if (isChangedForm) {
      setSelectedDraftStandardJob(v || undefined);
      setShowConfirmSaveChanges(true);
      setIsChangedIndustryStandardJob(true);
    } else {
      previousIndustryStandardJobRef.current = v;
      setIndustryStandardJob(v || undefined);
      onResetForm();
    }
  };

  const revertToPreviousIndustryStandardJob = useCallback(() => {
    if (previousIndustryStandardJobRef.current) {
      setIndustryStandardJob(undefined);
      setTimeout(() => {
        setIndustryStandardJob(previousIndustryStandardJobRef.current);
        onResetForm();
      }, 0);
      setIsChangedIndustryStandardJob(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handViewDetail = () => {
    setIsChangedError(true);
    setIsShowChangedDetail(false);
  };
  return (
    <>
      <Dialog open={open} fullScreen={true} onClose={onClose}>
        <DialogContent
          sx={{
            backgroundColor: "#F8F8FE",
            marginBottom: 6,
            paddingBottom: 8,
            ...(!organization?.enableStandardsLibrary && {
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            })
          }}
        >
          <StandardLibraryCheck>
            <FormProvider {...form}>
              <Container
                sx={{
                  height: "100%"
                }}
                component={"form"}
                onSubmit={handleSubmit(onSubmit)}
                id='job-benchmarking-form'
              >
                <Box height='100%' display={"flex"} flexDirection={"column"} gap={3}>
                  <Typography
                    variant='h5'
                    sx={{
                      fontWeight: 600
                    }}
                  >
                    {translate("settingsViews.jobBenchmarking.title")}
                  </Typography>

                  <Grid container spacing={8}>
                    <Grid item xs={6}>
                      <JobSelector
                        label={translate("settingsViews.jobBenchmarking.yourJob")}
                        items={jobList}
                        value={yourJob}
                        getItemLabel={(v: SettingsJob) => v.name || ""}
                        callApi={false}
                        multiple={false}
                        clearable={false}
                        onChange={onYourJobChange as any}
                        onInputChange={handleInputChangeJob as any}
                        translate={translate}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <JobSelector<IJobItem>
                        label={translate("settingsViews.jobBenchmarking.klayoIntelligenceJob")}
                        items={jobStandardLibrary}
                        value={industryStandardJob}
                        getItemLabel={(v) => v.name || ""}
                        callApi={false}
                        multiple={false}
                        clearable={false}
                        onChange={onStandardJobChange as any}
                        onInputChange={handleInputChangeStandardJob as any}
                        translate={translate}
                      />
                    </Grid>
                  </Grid>
                  <Box pb={5} flex={1}>
                    {renderContent()}
                  </Box>
                </Box>
              </Container>
            </FormProvider>
          </StandardLibraryCheck>
        </DialogContent>
        <BottomAppBar>
          <Container>
            <Toolbar>
              <Button
                label={translate("settingsViews.jobBenchmarking.close")}
                variant='outlined'
                size='md'
                startIcon={<CloseIcon />}
                onClick={onShowConfirmClose as any}
                disabled={isLoading}
              />
              <Box sx={{ flexGrow: 1 }}></Box>
              {organization?.enableStandardsLibrary && (
                <Button
                  size='md'
                  variant='filled'
                  label={translate("settingsViews.jobBenchmarking.applyChanges")}
                  disabled={!isChangedForm || isLoading}
                  type='submit'
                  form='job-benchmarking-form'
                  isLoading={isLoading}
                />
              )}
            </Toolbar>
          </Container>
        </BottomAppBar>
      </Dialog>
      {isShowChangedDetail && (
        <AppSnackbar
          open={true}
          type={"error"}
          message={
            <Typography hidden={competenciesError.length === 0}>
              {translate("settingsViews.jobBenchmarking.updateJobFailed")}
            </Typography>
          }
          action={
            <>
              <div
                style={{
                  marginLeft: "auto",
                  marginRight: "0.5rem",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
                hidden={competenciesError.length === 0}
                className='klayo-snackbar_text'
                onClick={handViewDetail}
              >
                {translate("settingsViews.jobBenchmarking.viewDetails")}
              </div>
              <CloseIcon
                style={{
                  cursor: "pointer",
                  width: "16px",
                  marginLeft: "auto"
                }}
                onClick={() => setIsShowChangedDetail(false)}
              />
            </>
          }
        />
      )}
      {showConfirmSaveChanges && (
        <ConfirmationDialog
          title={translate("settingsViews.jobBenchmarking.saveChanges")}
          theme={theme}
          question={translate("settingsViews.jobBenchmarking.doYouWantToApply")}
          cancelButton={translate("settingsViews.jobBenchmarking.dontSave")}
          acceptButton={translate("settingsViews.jobBenchmarking.applyChanges")}
          onCancel={onCancelApplyChanges}
          onAccept={onAcceptApplyChanges}
        />
      )}
      <ImportCompeteniesErrorDialog
        open={isChangedError}
        theme={theme}
        title={translate("settingsViews.jobBenchmarking.updateJobFailed")}
        competencies={competenciesError}
        description={translate("settingsViews.jobBenchmarking.updateFailedCompetencies", {
          count:
            competenciesError.length > 1
              ? translate("settingsViews.jobBenchmarking.competencies")
              : translate("settingsViews.jobBenchmarking.competency"),
          pronoun:
            competenciesError.length > 1
              ? translate("settingsViews.jobBenchmarking.they")
              : translate("settingsViews.jobBenchmarking.it"),
          verb:
            competenciesError.length > 1
              ? translate("settingsViews.jobBenchmarking.exist")
              : translate("settingsViews.jobBenchmarking.exists")
        })}
        onClose={() => setIsChangedError(false)}
      />
    </>
  );
};

export default JobBenchmarking;

const BottomAppBar = styled(AppBar)`
  bottom: 0;
  top: unset;
  background-color: #f8f8fe;
`;
